import { isAccountLinked, filterAccountsByRestrictions, analyzeFipAndAddToStableList } from './helper.utils';
import {
	getTargetedAccounts,
	mergeRestrictionsIntoAccounts,
	filterLinkedAccountsByRestrictions,
	isManualAccCheckEnabled,
	getTargetedFips,
	isDisplayOnlyTargetedFipsEnabled,
	isTerminateOnNoAccountFoundEnabled,
} from './targetedAccounts.utils';
import { trackEvent } from './tracking.utils';
import {
	captureDiscoveryRecordNotFound,
	captureDiscoveryCompleted,
	captureDiscoveryError,
	captureAllDiscoveriesCompleted,
} from './analytics/posthog.utils';

/**
 * Filters accounts by specific targeted account numbers
 * @param {Array} accounts - List of accounts to filter
 * @param {string} fid - FIP ID to match against
 * @returns {Array} Filtered accounts matching targeted restrictions
 */
const filterByTargetedAccountNumbers = (accounts, fid) => {
	if (!global?.redirectAccountRestrictions || !Array.isArray(global.redirectAccountRestrictions)) {
		return accounts;
	}
	
	return accounts.filter(acc => {
		return global.redirectAccountRestrictions.some(targetedAcc => {
			const discoveredLast4 = acc.maskedAccNumber?.slice(-4);
			const targetedLast4 = targetedAcc.maskedAccNumber;
			
			return targetedAcc.fipId === fid &&
				acc.FIType === targetedAcc.fiType &&
				acc.accType === targetedAcc.accType &&
				discoveredLast4 === targetedLast4;
		});
	});
};

/**
 * Simulates API latency with a random delay
 * @param {number} minDelay - Minimum delay in milliseconds
 * @param {number} maxDelay - Maximum delay in milliseconds
 * @returns {Promise} Promise that resolves after the delay
 */
const simulateLatency = (minDelay = 5000, maxDelay = 8000) => {
	const delay =
		Math.floor(Math.random() * (maxDelay - minDelay + 1)) + minDelay;
	return new Promise((resolve) => setTimeout(resolve, delay));
};

/**
 * Gets linked accounts that were not discovered for a specific FIP
 * @param {Array} linkedaccFull - Full list of linked accounts
 * @param {string} fid - FIP ID
 * @returns {Array} Array of linked accounts not discovered
 */
const getLinkedAccountsNotDiscovered = (linkedaccFull, fid) => {
	return linkedaccFull
		.filter((el) => el.fipId === fid)
		.map(({ accType, accRefNumber, maskedAccNumber, FIType, fipId }) => ({
			accType,
			accRefNumber,
			maskedAccNumber,
			FIType,
			fipId,
		}));
};

// getTargetedAccounts function moved to targetedAccounts.utils.js

/**
 * Prepares FIP identifiers for account discovery
 * @param {Object} fipIdentifiersInfo - FIP identifier information
 * @param {string} phoneNumber - User's phone number
 * @param {string} pan - User's PAN
 * @param {string} dob - User's DOB
 * @param {boolean} isPanRequired - Whether PAN is required
 * @param {boolean} isDobRequired - Whether DOB is required
 * @returns {Array} Array of FIP identifiers
 */
const prepareFipIdentifiers = (
	fipIdentifiersInfo,
	phoneNumber,
	pan,
	dob,
	isPanRequired,
	isDobRequired
) => {
	const fipIdentifiers = [];
	let idValue = phoneNumber;

	for (const [key, value] of Object.entries(fipIdentifiersInfo)) {
		if (key === 'PAN') {
			idValue = pan;
		}
		if (key === 'DOB') {
			idValue = dob;
		}
		if (
			(key === 'PAN' && !isPanRequired) ||
			(key === 'DOB' && !isDobRequired)
		) {
			continue;
		}
		fipIdentifiers.push({ category: value, type: key, value: idValue });
	}

	return fipIdentifiers;
};

/**
 * Processes discovered accounts for a FIP
 * @param {Object} res - API response
 * @param {Array} linkedaccFull - Full list of linked accounts
 * @param {string} fid - FIP ID
 * @param {string} phoneNumber - User's phone number
 * @returns {Object} Processed FIP data
 */
const processDiscoveredAccounts = (res, linkedaccFull, fid, phoneNumber) => {
	analyzeFipAndAddToStableList(fid, res.DiscoveredAccounts, linkedaccFull);
	const notLinkedList = res.DiscoveredAccounts.filter(
		(acc) => !isAccountLinked(acc, linkedaccFull,fid)
	);
	const filterAccounts = filterAccountsByRestrictions(notLinkedList);
	let notLinkedDiscoveredAccounts = global?.accountRestrictions
		? filterAccounts
		: notLinkedList;
	
	// Apply targeted account number filtering ONLY if in targeted discovery mode
	// userClickedAddMore = true means user clicked "Add More" and can discover ANY accounts
	// Note: allowAnyAccountOnNoMatch should NOT disable filtering - it only allows fallback after retry exhaustion
	const shouldFilterTargeted = global?.redirectAccountRestrictions && 
		!global?.userClickedAddMore;
	
	if (shouldFilterTargeted) {
		notLinkedDiscoveredAccounts = filterByTargetedAccountNumbers(notLinkedDiscoveredAccounts, fid);
	}

	// Add fipId to each discovered account for proper validation
	notLinkedDiscoveredAccounts = notLinkedDiscoveredAccounts.map(acc => ({ ...acc, fipId: fid }));

	// Track events
	if (notLinkedList.length > 0) {
		trackEvent('UNLINKED_ACCOUNTS_DISCOVERED', {
			fipId: fid,
			count: notLinkedList.length,
		});
	}
	if (res.DiscoveredAccounts.length - notLinkedList.length > 0) {
		trackEvent('LINKED_ACCOUNTS_DISCOVERED', {
			fipId: fid,
			count: res.DiscoveredAccounts.length - notLinkedList.length,
		});
	}

	// For fipObj, apply same filtering to all discovered accounts
	let discoveredAccountsForFipObj = global?.accountRestrictions
		? filterAccountsByRestrictions(res?.DiscoveredAccounts)
		: res.DiscoveredAccounts;
	
	if (shouldFilterTargeted) {
		discoveredAccountsForFipObj = filterByTargetedAccountNumbers(discoveredAccountsForFipObj, fid);
	}
	
	return {
		fipObj: {
			fipId: fid,
			discoveredAccounts: discoveredAccountsForFipObj,
			allDiscoveredAccounts: res.DiscoveredAccounts, // Store unfiltered for allowAnyAccountOnNoMatch
			phoneNumber: phoneNumber,
		},
		notLinkedDiscoveredAccounts,
		noResultsFips: [],
	};
};

/**
 * Processes failed or no-record FIP response
 * @param {Object} res - API response
 * @param {Array} linkedaccFull - Full list of linked accounts
 * @param {string} fid - FIP ID
 * @param {string} phoneNumber - User's phone number
 * @returns {Object} Processed FIP data
 */
const processFailedFipResponse = (res, linkedaccFull, fid, phoneNumber) => {
	const linkedAccountsNotDiscovered = getLinkedAccountsNotDiscovered(
		linkedaccFull,
		fid
	);
	let discoveredAccountsForFipObj = global?.accountRestrictions
		? filterAccountsByRestrictions(linkedAccountsNotDiscovered)
		: linkedAccountsNotDiscovered;
	
	// Apply targeted account number filtering ONLY if in targeted discovery mode
	// userClickedAddMore = true means user clicked "Add More" and can discover ANY accounts
	// Note: allowAnyAccountOnNoMatch should NOT disable filtering - it only allows fallback after retry exhaustion
	const shouldFilterTargeted = global?.redirectAccountRestrictions && 
		!global?.userClickedAddMore;
	
	if (shouldFilterTargeted) {
		discoveredAccountsForFipObj = filterByTargetedAccountNumbers(discoveredAccountsForFipObj, fid);
	}
	
	// Note: linkedAccountsNotDiscovered already have fipId from getLinkedAccountsNotDiscovered
	
	const fipObj = {
		fipId: fid,
		discoveredAccounts: discoveredAccountsForFipObj,
		allDiscoveredAccounts: linkedAccountsNotDiscovered, // Store unfiltered for allowAnyAccountOnNoMatch
		phoneNumber: phoneNumber,
	};

	const noResultsFips = [];
	if (discoveredAccountsForFipObj.length === 0) {
		noResultsFips.push(fid);
	}

	return { fipObj, notLinkedDiscoveredAccounts: [], noResultsFips };
};

/**
 * Updates account discovery state
 * @param {Object} prevState - Previous state
 * @param {Object} fipData - FIP data to update
 * @param {string} phoneNumber - User's phone number
 * @param {Function} history - History object for navigation
 * @param {Map} pendingDiscoveries - Map of ongoing discoveries
 * @returns {Object} Updated state
 */
const updateAccountDiscoveryState = (
	prevState,
	fipData,
	phoneNumber,
	history,
	pendingDiscoveries
) => {
	const { fipObj, notLinkedDiscoveredAccounts, noResultsFips } = fipData;
	const fid = fipObj.fipId;

	let newDiscoveredAccounts = [...prevState.discoveredAccounts];
	let newNoResultsFips = [...(prevState.noDiscoveredAccounts?.fips || [])];

	// Check if accounts were filtered to zero by general accountRestrictions (FIType/accType)
	// This is different from redirectAccountRestrictions (targeted specific accounts)
	const accountsFilteredByTypeRestrictions = global?.accountRestrictions && 
		!global?.redirectAccountRestrictions && // Not in targeted mode
		fipObj.discoveredAccounts.length === 0 && 
		fipObj.allDiscoveredAccounts?.length > 0;
	
	// Only add to discovered accounts if we have accounts
	// For redirectAccountRestrictions (targeted mode), allowAnyAccountOnNoMatch OR disabledAddNew settings may show unfiltered
	// For general accountRestrictions, if filtered to zero, treat as no results
	const willShowUnfilteredInTargetedMode = global?.redirectAccountRestrictions &&
		(global.sdkOptions?.allowAnyAccountOnNoMatch === 'true' || 
		 global.sdkOptions?.disabledAddNew !== 'true');
	
	const hasAccounts = fipObj.discoveredAccounts.length > 0 ||
		(!accountsFilteredByTypeRestrictions && willShowUnfilteredInTargetedMode && fipObj.allDiscoveredAccounts?.length > 0);
	
	if (hasAccounts) {
		newDiscoveredAccounts = [
			...newDiscoveredAccounts.filter((acc) => acc.fipId !== fid),
			fipObj,
		].sort((a, b) => b.discoveredAccounts.length - a.discoveredAccounts.length);
			
		newNoResultsFips = newNoResultsFips.filter(fipId => fipId !== fid);
	} else {
		// Remove from discovered accounts if it was there
		newDiscoveredAccounts = newDiscoveredAccounts.filter(
			(acc) => acc.fipId !== fid
		);
		// Add to no results if not already there
		if (!newNoResultsFips.includes(fid)) {
			newNoResultsFips.push(fid);
		}
	}
	let allNotLinkedDiscovered = newDiscoveredAccounts.flatMap((fip) =>
		fip.discoveredAccounts.filter(
			(acc) => !isAccountLinked(acc, prevState.linkedAccountsFull, fip.fipId)
		)
	);
	if (isDisplayOnlyTargetedFipsEnabled()) {
		const targetedFips = getTargetedFips();
		if (targetedFips.length > 0) {
			allNotLinkedDiscovered = allNotLinkedDiscovered.filter((acc) =>
				targetedFips.includes(acc.fipId)
			);
		}
	}

	// Check if no accounts found across all FIPs
	const noAccountsFoundAcrossAllFips = 
		isTerminateOnNoAccountFoundEnabled() &&
		history?.location?.state?.data &&
		allNotLinkedDiscovered.length === 0 &&
		prevState.discoveredAccounts.length === 0 &&
		newDiscoveredAccounts.length === 0 &&
		pendingDiscoveries.size === 0;
	
	if (noAccountsFoundAcrossAllFips) {
		const allNoResultFips = [...newNoResultsFips];
		if (!allNoResultFips.includes(fid) && noResultsFips.includes(fid)) {
			allNoResultFips.push(fid);
		}

		history.push('/exit', {
			fipIds: allNoResultFips
		});
	}
	// Check account restrictions satisfaction if they exist
	if (global.redirectAccountRestrictions && history?.location?.state?.data && prevState.linkedAccountsFull) {
		const allRestrictionsSatisfied =
			global.redirectAccountRestrictions.every((restriction) =>
				prevState.linkedAccountsFull.some(
					(account) => {
						const fipIdMatch = account.fipId === restriction.fipId;
						const fiTypeMatch = account.FIType === restriction.fiType;
						const accTypeMatch = account.accType === restriction.accType;
						// Compare last 4 digits: restriction might have just "6086" while account has "XXXXX6086"
						const maskedMatch = account.maskedAccNumber === restriction.maskedAccNumber || 
						                   account.maskedAccNumber?.slice(-4) === restriction.maskedAccNumber;
						return fipIdMatch && fiTypeMatch && accTypeMatch && maskedMatch;
					}
				)
			);

		if (allRestrictionsSatisfied) {
		// Filter linked accounts to only include those matching restrictions
		// UNLESS user manually clicked "Add More" - in that case, show all accounts
		let accountsToPass = global.userClickedAddMore 
			? prevState.linkedAccountsFull 
			: filterLinkedAccountsByRestrictions(prevState.linkedAccountsFull);
		if (isDisplayOnlyTargetedFipsEnabled()) {
			const targetedFips = getTargetedFips();
			if (targetedFips.length > 0) {
				accountsToPass = accountsToPass.filter((acc) =>
					targetedFips.includes(acc.fipId)
				);
			}
		}
		
		// Clear targeted mode flag since restrictions are satisfied
		// But keep userClickedAddMore if user manually clicked it (persist their choice)
		if (!global.userClickedAddMore) {
			delete global.isTargetedDiscoveryMode;
		}
		global.hasReachedConsent = true; // Prevent modal from showing on back navigation
		
		// Pass accounts to consent page
		history.push({
			pathname: `/payment-consent`,
			state: { data: accountsToPass, preselectAccounts: accountsToPass },
		});
		return prevState; // Early return if redirecting
		}
	}

	// Apply targeted account filtering if in targeted discovery mode
	// Skip filtering only if user clicked "Add More"
	// Note: allowAnyAccountOnNoMatch should NOT disable filtering - it only allows fallback after retry exhaustion
	const shouldApplyTargetedFiltering = global?.redirectAccountRestrictions &&
		!global?.userClickedAddMore;
	
	// If user clicked "Add More", show ALL accounts but merge restrictions for matched ones
	const shouldMergeRestrictions = global?.redirectAccountRestrictions &&
		global?.userClickedAddMore;
	
	let finalDiscoveredAccounts;
	if (shouldApplyTargetedFiltering) {
		// Filter to show only targeted accounts
		finalDiscoveredAccounts = getTargetedAccounts(newDiscoveredAccounts, global.redirectAccountRestrictions);
	} else if (shouldMergeRestrictions) {
		// Show ALL accounts but merge restrictions for matched ones
		finalDiscoveredAccounts = mergeRestrictionsIntoAccounts(newDiscoveredAccounts, global.redirectAccountRestrictions);
	} else {
		// Show all accounts as-is
		finalDiscoveredAccounts = newDiscoveredAccounts;
	}

	// For targeted accounts, also add FIPs that had accounts but no targeted matches
	if (shouldApplyTargetedFiltering) {
		const fipHadAccounts = newDiscoveredAccounts.some(fip => fip.fipId === fid && fip.discoveredAccounts.length > 0);
		const fipHasTargetedAccounts = finalDiscoveredAccounts.some(fip => fip.fipId === fid && fip.discoveredAccounts.length > 0);
		
		if (fipHadAccounts && !fipHasTargetedAccounts && !newNoResultsFips.includes(fid)) {
			newNoResultsFips.push(fid);
		}
	}
	// Check if we should redirect
	if (
		global.sdkOptions?.hideNoAccountFound === 'true' &&
		history?.location?.state?.data &&
		prevState.discoveredAccounts.length === 0 &&
		finalDiscoveredAccounts.length === 0 &&
		pendingDiscoveries.size === 0
	) {
		history.push(`/home/?number=${phoneNumber}&isAddAccount=${true}`);
	}

	// Pre-select discovered accounts only for targeted discovery (fiuInfo accounts), not normal/manual flow
	let accountsToAdd = [];
	if (shouldApplyTargetedFiltering && !isManualAccCheckEnabled()) {
		accountsToAdd = notLinkedDiscoveredAccounts.map((account) => {
			const matchingRestriction = global.redirectAccountRestrictions?.find(
				(restriction) =>
					restriction.fipId === account.fipId &&
					restriction.fiType === account.FIType &&
					restriction.accType === account.accType &&
					(restriction.maskedAccNumber === account.maskedAccNumber ||
						account.maskedAccNumber?.slice(-4) === restriction.maskedAccNumber)
			);
			if (matchingRestriction) {
				return { ...account, restrictions: matchingRestriction };
			}
			return account;
		});
	}

	return {
		...prevState,
		discoveredAccounts: finalDiscoveredAccounts,
		isLoding: false,
		isDiscovering: pendingDiscoveries.size > 0,
		selectedAccounts: [
			...prevState.selectedAccounts.filter((acc) => acc.fipId !== fid),
			...accountsToAdd,
		],
		noDiscoveredAccounts: {
			fips: global.sdkOptions?.hideNoAccountFound && history?.location?.state?.data 
				? [] 
				: newNoResultsFips,
			phoneNumber: phoneNumber,
		},
		noAccountsFoundAcrossAllFips,
	};
};

/**
 * Handles discovery for a single FIP
 * @param {Object} params - Parameters for FIP discovery
 * @returns {Promise} Promise resolving to processed FIP data
 */
const handleFipDiscovery = async ({
	fid,
	fipIdentifiersInfo,
	phoneNumber,
	pan,
	dob,
	isPanRequired,
	isDobRequired,
	linkedaccFull,
	authApis,
	pendingDiscoveries,
}) => {
	const fipIdentifiers = prepareFipIdentifiers(
		fipIdentifiersInfo,
		phoneNumber,
		pan,
		dob,
		isPanRequired,
		isDobRequired
	);

	pendingDiscoveries.set(fid, true);
	const fipStartTime = Date.now();

	try {
		const res = await authApis.discoverAccountsAsync(fid, fipIdentifiers);
		
		if (!res) {
			throw new Error('No response from server');
		}

		let fipData;
		if (res?.status === 'SUCCESS') {
			fipData = processDiscoveredAccounts(res, linkedaccFull, fid, phoneNumber);
			captureDiscoveryCompleted(fid, {
				discoveryTime: Date.now() - fipStartTime,
				accountsFound: res?.DiscoveredAccounts?.length || 0,
				status: res?.status,
				isFirstFip: pendingDiscoveries.size === 1,
				totalPendingFips: pendingDiscoveries.size - 1,
				isAsync: true,
			});
		} else if (
			res?.status === 'RECORD-NOT-FOUND' ||
			res?.status === 'FAILURE'
		) {
			if (res?.status === 'RECORD-NOT-FOUND') {
				captureDiscoveryRecordNotFound(fid, {
					discoveryTime: Date.now() - fipStartTime,
					isFirstFip: pendingDiscoveries.size === 1,
					totalPendingFips: pendingDiscoveries.size - 1,
					isAsync: true,
				});
			} else {
				captureDiscoveryError(fid, {
					error: res?.message || 'Unknown error',
					discoveryTime: Date.now() - fipStartTime,
					isFirstFip: pendingDiscoveries.size === 1,
					isAsync: true,
				});
			}
			fipData = processFailedFipResponse(res, linkedaccFull, fid, phoneNumber);
		} else {
			if (res?.status === 'FAILURE' && res?.message === 'Session Error.') {
				throw new Error('Session Error');
			}
			fipData = {
				fipObj: {
					fipId: fid,
					discoveredAccounts: [],
					phoneNumber: phoneNumber,
				},
				notLinkedDiscoveredAccounts: [],
				noResultsFips: [fid],
			};
		}

		// Track completion of all discoveries if this was the last pending FIP
		if (pendingDiscoveries.size === 1) {
			captureAllDiscoveriesCompleted({
				totalTime: Date.now() - fipStartTime,
				totalFips: pendingDiscoveries.size,
				isAsync: true,
			});
		}

		return fipData;
	} catch (error) {
		captureDiscoveryError(fid, {
			error: error.message || 'Unknown error',
			discoveryTime: Date.now() - fipStartTime,
			isFirstFip: pendingDiscoveries.size === 1,
			isAsync: true,
		});
		throw error;
	} finally {
		pendingDiscoveries.delete(fid);
	}
};

export {
	handleFipDiscovery,
	updateAccountDiscoveryState,
	prepareFipIdentifiers,
	processDiscoveredAccounts,
	processFailedFipResponse,
	getLinkedAccountsNotDiscovered,
};
